import React, { useState, useEffect } from "react";
import { useAppContext } from "../AppContext";
import PaymentService from "../services/payment.service";
import { useTranslation } from "react-i18next";
import moment from "moment";
import useAxiosFetch from "../common/useAxiosFetch";
import LanguageSwitcher from "../common/LanguageSwitcher";

const PaymentPage = () => {
  const { currentUser } = useAppContext();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDomainCount, setSelectedDomainCount] = useState(null); // For KNMT plans
  const locale = i18n.language === 'nl' ? 'nl-NL' : 'en-US';

  const formatCurrency = (value, options = {}) => {
    const { roundUp = false, ...intlOptions } = options;
    const amount = roundUp ? Math.ceil(value) : value;

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: roundUp ? 0 : 2,
      maximumFractionDigits: roundUp ? 0 : 2,
      ...intlOptions,
    }).format(amount);
  };
  
  // Fetch subscription plans using useAxiosFetch
  const { data: allPlans } = useAxiosFetch("/api/subscription-plans");
  
  // Fetch projects to calculate dynamic KNMT pricing
  const { data: projects } = useAxiosFetch("/api/projects/byOrg/" + currentUser?.orgId);
  
  // Filter based on environment - show starter/premium for main, knmt-starter for knmt
  const plans = allPlans ? allPlans.filter(plan => {
    if (currentUser?.parentEnvironment === 'knmt') {
      return plan.planId === 'knmt-starter';
    }
    return plan.planId === 'starter' || plan.planId === 'premium';
  }) : [];

  // Set the first plan as default when plans are loaded
  useEffect(() => {
    if (plans.length > 0 && !selectedPlan) {
      setSelectedPlan(plans[0].planId);
    }
  }, [plans, selectedPlan]);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const status = await PaymentService.getSubscriptionStatus();
        setSubscriptionInfo(status);
      } catch (err) {
        console.error("Error fetching subscription status:", err);
      }
    };

    if (currentUser) {
      fetchSubscriptionStatus();
    }
  }, [currentUser]);

  // Initialize selected domain count for KNMT plans
  useEffect(() => {
    const plan = getSelectedPlanData();
    if (plan && plan.pricePerAdditionalDomain && projects && selectedDomainCount === null) {
      setSelectedDomainCount(projects.length);
    }
  }, [selectedPlan, projects, selectedDomainCount]);

  // Initialize selected domain count for KNMT plans
  useEffect(() => {
    const plan = getSelectedPlanData();
    if (plan && plan.pricePerAdditionalDomain && projects && selectedDomainCount === null) {
      setSelectedDomainCount(projects.length);
    }
  }, [selectedPlan, projects, selectedDomainCount]);

  const handlePayment = async () => {
    setLoading(true);
    setError("");

    try {
      const plan = getSelectedPlanData();
      const domainCount = plan?.pricePerAdditionalDomain ? selectedDomainCount : undefined;
      
      const response = await PaymentService.createPayment(
        currentUser.environmentId,
        selectedPlan,
        domainCount,
        i18n.language || 'en'
      );
      
      // Redirect to Mollie checkout
      if (response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Error creating payment");
      setLoading(false);
    }
  };

  const getSelectedPlanData = () => {
    return plans.find(p => p.planId === selectedPlan);
  };

  const getPrice = () => {
    const plan = getSelectedPlanData();
    if (!plan) return 0;
    
    // For KNMT plans with dynamic pricing, calculate based on selected domain count
    if (plan.pricePerAdditionalDomain && plan.features.baseProjects !== undefined) {
      const domainCount = selectedDomainCount || (projects ? projects.length : plan.features.baseProjects);
      const additionalDomains = Math.max(0, domainCount - plan.features.baseProjects);
      return plan.priceYearly + (additionalDomains * plan.pricePerAdditionalDomain);
    }
    
    // Standard pricing
    return plan.priceYearly;
  };

  const getVAT = () => {
    return getPrice() * 0.21;
  };

  const getTotalWithVAT = () => {
    return getPrice() + getVAT();
  };

  const getAdditionalDomainCount = () => {
    const plan = getSelectedPlanData();
    if (!plan || plan.features.baseProjects === undefined) {
      return 0;
    }
    const domainCount = selectedDomainCount || (projects ? projects.length : plan.features.baseProjects);
    return Math.max(0, domainCount - plan.features.baseProjects);
  };



  const getNewSubscriptionEndDate = (planId) => {
    if (!subscriptionInfo) return null;
    
    const now = new Date().getTime();
    let startDate = now;
    
    // If there's an active subscription, extend from current expiry date
    if (subscriptionInfo.subscriptionExpires && subscriptionInfo.subscriptionExpires > now) {
      startDate = subscriptionInfo.subscriptionExpires;
    }
    
    // All subscriptions are yearly (365 days)
    const durationDays = 365;
    const newEndDate = new Date(startDate + (durationDays * 24 * 60 * 60 * 1000));
    
    return newEndDate;
  };

  // Check subscription status
  const getSubscriptionStatus = () => {
    if (!subscriptionInfo) return { hasActiveSubscription: false, expirationInfo: null };

    const now = new Date().getTime();
    
    // Check if there's an active subscription (not expired)
    const hasActiveSubscription = subscriptionInfo.subscriptionExpires && subscriptionInfo.subscriptionExpires > now;
    
    // If active subscription, don't show expiration warnings
    if (hasActiveSubscription) {
      return {
        hasActiveSubscription: true,
        expirationInfo: null,
        currentPlanId: subscriptionInfo.subscriptionPlan?.planId
      };
    }

    // Check what expired
    const subscriptionExpired = subscriptionInfo.subscriptionExpires && subscriptionInfo.subscriptionExpires < now;
    const trialExpired = subscriptionInfo.trialExpires && subscriptionInfo.trialExpires < now;

    // Show subscription expiration if it exists (takes priority)
    if (subscriptionExpired) {
      return {
        hasActiveSubscription: false,
        expirationInfo: {
          type: 'subscription',
          date: subscriptionInfo.subscriptionExpires,
          message: t('Your subscription has expired. Please renew to continue using Risqui.')
        }
      };
    }

    // Otherwise show trial expiration
    if (trialExpired) {
      return {
        hasActiveSubscription: false,
        expirationInfo: {
          type: 'trial',
          date: subscriptionInfo.trialExpires,
          message: t('Your trial period has ended. Subscribe to continue using Risqui.')
        }
      };
    }

    return { hasActiveSubscription: false, expirationInfo: null };
  };

  const subscriptionStatus = getSubscriptionStatus();
  const expirationInfo = subscriptionStatus.expirationInfo;
  const additionalDomainCount = getAdditionalDomainCount();

  const selectedPlanData = getSelectedPlanData();
  const isDomainPricing = selectedPlanData?.planId === 'knmt-starter' && selectedPlanData?.pricePerAdditionalDomain && projects;
  const domainLabel = (count) => count === 1 ? t('domain') : t('domains').toLowerCase();

  return (
    <div id="login-container" className="payment-container">
      <div className="container">
        <div className="col-md-12">
          <div id="login-content">
            <div className="card card-container">
              <LanguageSwitcher />
              <div id="content-title">
                {subscriptionStatus.hasActiveSubscription 
                  ? t('Upgrade Your Plan') 
                  : t('Subscription Required')}
              </div>

              <div className="pay-intro">
                {subscriptionStatus.hasActiveSubscription ? (
                  <>
                    <div className="pay-status">
                      <div className="pay-status-head">
                        <i className="icon-check-circle" />
                        <strong>{t('Current Plan')}:</strong>
                        <span className="pay-status-plan">
                          {subscriptionStatus.currentPlanId}
                          <span>{' '}({t('Yearly')})</span>
                        </span>
                      </div>
                      <div className="pay-status-expiry">
                        {t('Expires on')}: {moment(subscriptionInfo.subscriptionExpires).format('LL')}
                        {' '}({moment(subscriptionInfo.subscriptionExpires).fromNow()})
                      </div>
                    </div>
                    <p>
                      {subscriptionStatus.currentPlanId === 'starter' 
                        ? t('Upgrade to Premium for unlimited organizations, domains, and more AI calls.')
                        : t('Choose a plan to extend or upgrade your subscription.')}
                    </p>
                  </>
                ) : expirationInfo ? (
                  <>
                    <p>{expirationInfo.message}</p>
                    <div className="pay-expired">
                      <i className="icon-info" />
                      <div>
                        <strong>
                          {expirationInfo.type === 'trial' ? t('Trial expired') : t('Subscription expired')}:
                        </strong>{' '}
                        {moment(expirationInfo.date).format('LLLL')}
                        <span className="pay-muted"> ({moment(expirationInfo.date).fromNow()})</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p>{t('Choose your subscription plan to continue using Risqui.')}</p>
                )}
              </div>

              {/* Plan Selection */}
              <div className={`pay-plans ${plans.length === 2 ? 'two' : ''}`}>
                {plans.map(plan => {
                  const price = plan.priceYearly;
                  const isKnmtPlan = plan.planId === 'knmt-starter';
                  const isSelected = selectedPlan === plan.planId;
                  const isCurrentPlan = subscriptionStatus.hasActiveSubscription && plan.planId === subscriptionStatus.currentPlanId;
                  const isUpgrade = subscriptionStatus.hasActiveSubscription && plan.planId === 'premium' && subscriptionStatus.currentPlanId === 'starter';
                  const newEndDate = getNewSubscriptionEndDate(plan.planId);
                  
                  return (
                    <div
                      key={plan.planId}
                      className={`pay-plan ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedPlan(plan.planId)}
                    >
                      <span className="pay-plan-check"><i className="icon-check" /></span>

                      {isCurrentPlan && <span className="pay-plan-badge current">{t('Current')}</span>}
                      {isUpgrade && <span className="pay-plan-badge">{t('Recommended')}</span>}
                      
                      <div className="pay-plan-name">{plan.name}</div>
                      
                      <div className="pay-plan-price">
                        {formatCurrency(price, { roundUp: isKnmtPlan })}{isKnmtPlan && plan.pricePerAdditionalDomain && '+'}
                        <span>/{t('year')}</span>
                      </div>
                      {isKnmtPlan && plan.pricePerAdditionalDomain && (
                        <div className="pay-muted">
                          + {formatCurrency(plan.pricePerAdditionalDomain, { roundUp: true })} {t('per additional domain')}
                        </div>
                      )}
                      <div className="pay-muted">{t('excl. VAT')}</div>

                      {(plan.description || plan.descriptionNL) && (
                        <p className="pay-plan-desc">
                          {i18n.language === 'nl' && plan.descriptionNL ? plan.descriptionNL : plan.description}
                        </p>
                      )}

                      <ul className="pay-plan-features">
                        <li>
                          {plan.features.maxOrganizations === -1 
                            ? t('Unlimited organizations')
                            : `${plan.features.maxOrganizations} ${t('organization')}${plan.features.maxOrganizations > 1 ? 's' : ''}`
                          }
                        </li>
                        <li>
                          {plan.features.maxProjects === -1 
                            ? (isKnmtPlan && plan.pricePerAdditionalDomain 
                                ? `${t('Pay per domain')} (${formatCurrency(plan.pricePerAdditionalDomain, { roundUp: true })} ${t('per additional domain')})`
                                : t('Unlimited domains'))
                            : `${plan.features.maxProjects} ${t('domain')}${plan.features.maxProjects > 1 ? 's' : ''}`
                          }
                        </li>
                        <li>
                          {plan.features.maxUsers === -1 
                            ? t('Unlimited users')
                            : `${plan.features.maxUsers} ${t('user')}${plan.features.maxUsers > 1 ? 's' : ''}`
                          }
                        </li>
                        <li>{t('Unlimited risks')}</li>
                        <li>
                          {plan.features.aiCallsPerWeek === -1 
                            ? t('Unlimited AI calls')
                            : `${plan.features.aiCallsPerWeek} ${t('AI calls per week')}`
                          }
                        </li>
                      </ul>
                      
                      {newEndDate && (
                        <div className="pay-plan-renewal">
                          <span>
                            {isCurrentPlan ? t('Extends subscription to') : t('Subscription active until')}
                          </span>
                          <strong>{moment(newEndDate).format('LL')}</strong>
                          <span className="pay-muted">
                            ({subscriptionStatus.hasActiveSubscription 
                              ? t('Expires') + ' ' + moment(newEndDate).fromNow()
                              : t('for') + ' ' + Math.ceil(moment(newEndDate).diff(moment(), 'months', true)) + ' ' + t('months')
                            })
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Domain Count Selector for KNMT plans */}
              {isDomainPricing && (
                <div className="pay-panel">
                  <h4><i className="icon-sliders" />{t('Select number of domains')}</h4>
                  <p className="pay-muted">
                    {t('You currently have')} <strong>{projects.length}</strong> {domainLabel(projects.length)}.&nbsp;
                    {t('Choose how many domains you want to include in your subscription.')}
                  </p>
                  <div className="pay-domains">
                    <label htmlFor="pay-domain-count">{t('Number of domains')}:</label>
                    <input 
                      id="pay-domain-count"
                      type="number" 
                      className="form-control"
                      min={projects.length}
                      max={50}
                      value={selectedDomainCount || projects.length}
                      onChange={(e) => setSelectedDomainCount(Math.max(projects.length, parseInt(e.target.value) || projects.length))}
                    />
                    <div className="pay-chips">
                      {[projects.length, projects.length + 2, projects.length + 5].filter((v, i, arr) => arr.indexOf(v) === i).map(count => (
                        <button
                          key={count}
                          type="button"
                          className={`pay-chip ${selectedDomainCount === count ? 'active' : ''}`}
                          onClick={() => setSelectedDomainCount(count)}
                        >
                          {count} {domainLabel(count)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pay-muted pay-domains-hint">
                    <i className="icon-info" />
                    <span>
                      {t('Price')}: {formatCurrency(selectedPlanData.priceYearly)} + ({additionalDomainCount} × {formatCurrency(selectedPlanData.pricePerAdditionalDomain)}) = <strong>{formatCurrency(getPrice())}</strong> {t('excl. VAT')}
                    </span>
                  </div>
                </div>
              )}

              {/* Price Breakdown */}
              {selectedPlan && plans.length > 0 && (
                <div className="pay-panel pay-summary">
                  <h4>{t('Price Breakdown')}</h4>
                  {isDomainPricing ? (
                    <>
                      <div className="pay-row">
                        <span>{selectedPlanData?.name} - Base (1 {t('domain').toLowerCase()})</span>
                        <span>{formatCurrency(selectedPlanData.priceYearly)}</span>
                      </div>
                      {additionalDomainCount > 0 && (
                        <div className="pay-row indent">
                          <span>
                            + {additionalDomainCount} {t('additional')} {domainLabel(additionalDomainCount)} × {formatCurrency(selectedPlanData.pricePerAdditionalDomain)}
                          </span>
                          <span>{formatCurrency(additionalDomainCount * selectedPlanData.pricePerAdditionalDomain)}</span>
                        </div>
                      )}
                      <div className="pay-row subtotal">
                        <span>{t('Subtotal')} ({t('Yearly').toLowerCase()})</span>
                        <span>{formatCurrency(getPrice())}</span>
                      </div>
                    </>
                  ) : (
                    <div className="pay-row">
                      <span>{selectedPlanData?.name} ({t('Yearly').toLowerCase()})</span>
                      <span>{formatCurrency(getPrice())}</span>
                    </div>
                  )}
                  <div className="pay-row indent pay-muted">{t('excl. VAT')}</div>
                  <div className="pay-row">
                    <span>{t('VAT (21%)')}</span>
                    <span>{formatCurrency(getVAT())}</span>
                  </div>
                  <div className="pay-row total">
                    <span>{t('Total incl. VAT')}</span>
                    <span>{formatCurrency(getTotalWithVAT())}</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <button 
                className="btn btn-outline-primary btn-block login-submit"
                onClick={handlePayment}
                disabled={loading || !selectedPlan || plans.length === 0}
              >
                {loading && (
                  <span className="spinner-border spinner-border-sm" />
                )}
                {loading 
                  ? t('Processing...') 
                  : selectedPlan && plans.length > 0
                    ? `${subscriptionStatus.hasActiveSubscription && selectedPlan !== subscriptionStatus.currentPlanId ? t('Upgrade') : t('Pay')} ${formatCurrency(getTotalWithVAT())} ${t('incl. VAT')}`
                    : t('Subscribe Now')
                }
              </button>

              <div className="auth-footnote">
                <p>
                  <i className="icon-shield" /> {t('Secure payment processed by Mollie. You will be redirected to complete your payment.')}
                </p>
                <p>
                  {t('Questions? Contact us at')}{' '}
                  <a href="mailto:info@risqui.nl">info@risqui.nl</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
